import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Card } from '@sorare/core/src/atoms/icons/Card';
import { Dollar } from '@sorare/core/src/atoms/icons/Dollar';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { EdgeRectangleTrapezoid } from '@sorare/core/src/atoms/ui/EdgeRectangleTrapezoid';
import {
  AnyRewardConfig,
  Variant as NewRewardConfigVariant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import type { unitMapping } from '@sorare/core/src/lib/style';

import type { Rewards_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const displayableReward = [
  'MonetaryRewardConfig',
  'ConversionCreditRewardConfig',
  'CardRewardConfig',
  'ProbabilisticBundleRewardConfig',
  'CardShardRewardConfig',
];

type Props = {
  so5LeaderboardGroupInterface: Rewards_so5LeaderboardGroupInterface;
  simplified?: boolean;
  variant?: 'grouped' | 'split';
};

const Separator = styled.div`
  width: 1px;
  height: var(--double-unit);
  background-color: var(--c-nd-150);
`;

const StyledHorizontal = styled(Horizontal)`
  padding: var(--half-unit) 0;
  & ${Separator} {
    &:last-child {
      display: none;
    }
  }
`;

const RewardIcon = ({
  anyRewardConfig,
  size = 1.5,
}: {
  anyRewardConfig: Rewards_so5LeaderboardGroupInterface['inSeasonTotalRewardConfigs'][number];
  size?: keyof typeof unitMapping;
}) => {
  if (isType(anyRewardConfig, 'MonetaryRewardConfig'))
    return <Dollar size={size} />;
  if (isType(anyRewardConfig, 'CardRewardConfig'))
    return <Card size={size} color="var(--c-rewards-purple)" />;
  if (isType(anyRewardConfig, 'ProbabilisticBundleRewardConfig'))
    return <Box size={size} gradient="red" />;
  if (isType(anyRewardConfig, 'CardShardRewardConfig'))
    return <Shards size={size} color="var(--c-green-400)" />;
  return (
    <RewardConfigImage
      size={2}
      anyRewardConfig={anyRewardConfig}
      rarity="mix"
    />
  );
};

const BottomRewardsContainer = styled.div`
  position: absolute;
  bottom: 0;
`;

const StyledBrandS = styled(BrandXXS)`
  min-height: var(--triple-unit);
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

export const Rewards = ({
  so5LeaderboardGroupInterface,
  simplified,
  variant,
}: Props) => {
  const {
    inSeasonTotalRewardConfigs,
    classicTotalRewardConfigs,
    seasonalities,
    primaryColor,
    hasLeaderboards,
  } = so5LeaderboardGroupInterface;

  const totalRewardConfigs = seasonalities.includes(
    So5LeaderboardSeasonality.IN_SEASON
  )
    ? inSeasonTotalRewardConfigs
    : classicTotalRewardConfigs;

  const filteredTotalRewardConfigs = totalRewardConfigs.filter(
    anyRewardConfig => {
      if (!displayableReward.includes(anyRewardConfig.__typename)) {
        return false;
      }
      if (simplified && !isType(anyRewardConfig, 'CardShardRewardConfig')) {
        return false;
      }
      return true;
    }
  );

  if (!filteredTotalRewardConfigs.length && hasLeaderboards) {
    return null;
  }

  if (variant === 'grouped') {
    return (
      <BottomRewardsContainer>
        <EdgeRectangleTrapezoid
          padding="var(--half-unit) var(--triple-unit)"
          border={`linear-gradient(to bottom, ${primaryColor} 0%, #111F1C 70%)`}
        >
          <StyledHorizontal gap={2}>
            {!hasLeaderboards ? (
              <StyledBrandS>
                <FormattedMessage {...glossary.noGame} />
              </StyledBrandS>
            ) : (
              <>
                {filteredTotalRewardConfigs.map(anyRewardConfig => {
                  if (
                    isType(anyRewardConfig, 'MonetaryRewardConfig') ||
                    isType(anyRewardConfig, 'ConversionCreditRewardConfig')
                  ) {
                    return (
                      <Fragment key={anyRewardConfig.id}>
                        <AnyRewardConfig
                          anyRewardConfig={anyRewardConfig}
                          variant={NewRewardConfigVariant.TEXT}
                          rarity="mix"
                        />
                        <Separator />
                      </Fragment>
                    );
                  }
                  return (
                    <Fragment key={anyRewardConfig.id}>
                      <RewardConfigImage
                        anyRewardConfig={anyRewardConfig}
                        rarity="mix"
                      />
                      <Separator />
                    </Fragment>
                  );
                })}
              </>
            )}
          </StyledHorizontal>
        </EdgeRectangleTrapezoid>
      </BottomRewardsContainer>
    );
  }
  return (
    <Horizontal gap={0.5}>
      {filteredTotalRewardConfigs.map(anyRewardConfig => {
        const showText =
          isType(anyRewardConfig, 'MonetaryRewardConfig') ||
          isType(anyRewardConfig, 'ConversionCreditRewardConfig');
        return (
          <Chip key={anyRewardConfig.id}>
            <RewardIcon anyRewardConfig={anyRewardConfig} />
            {!!showText && (
              <RewardConfigText anyRewardConfig={anyRewardConfig} />
            )}
          </Chip>
        );
      })}
    </Horizontal>
  );
};

Rewards.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment Rewards_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      hasLeaderboards
      seasonalities
      primaryColor: color(variant: PRIMARY)
      inSeasonTotalRewardConfigs: totalRewardConfigs(
        highestConfigByType: true
        seasonality: IN_SEASON
        onlyPlayable: true
      ) {
        id
        ...RewardConfigText_anyRewardConfigInterface
        ...RewardConfigImage_anyRewardConfigInterface
      }
      classicTotalRewardConfigs: totalRewardConfigs(
        highestConfigByType: true
        seasonality: ALL_SEASONS
        onlyPlayable: true
      ) {
        id
        ...RewardConfigText_anyRewardConfigInterface
        ...RewardConfigImage_anyRewardConfigInterface
      }
    }
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<Rewards_so5LeaderboardGroupInterface>,
};
